import { css, cx } from "styled-system/css";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { cf } from "@/components/brand/sfturnos-styles";
import { DashboardPageShell } from "@/components/dashboard/dashboard-page-shell";
import { LoadingFrame } from "@/components/ui/loading-frame";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

type Org = { id: string; name: string; slug: string; ownerId: string };
type Member = {
  userId: string;
  email: string;
  role: string;
  professional: { id: string; userId: string; name: string; slug: string; avatarUrl: string | null; isActive: boolean } | null;
};

export function DashboardTeam() {
  const { refresh } = useAuth();
  const [org, setOrg] = useState<Org | null | "loading">("loading");
  const [role, setRole] = useState<string | null>(null);
  const [members, setMembers] = useState<Member[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [createName, setCreateName] = useState("");
  const [createSlug, setCreateSlug] = useState("");

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"admin" | "member">("member");
  const [inviteBusy, setInviteBusy] = useState(false);

  const canManage = role === "owner" || role === "admin";

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setErr(null);
      setOrg("loading");
      const r = await fetchWithSessionSync("/api/organizations/me", { credentials: "include" }, refresh);
      if (cancelled) return;
      if (!r.ok) {
        setOrg(null);
        setRole(null);
        setMembers([]);
        return;
      }
      const j = (await r.json()) as { organization: Org | null; role: string | null };
      setOrg(j.organization);
      setRole(j.role);
      if (j.organization) {
        const rm = await fetchWithSessionSync(`/api/organizations/${j.organization.id}/members`, { credentials: "include" }, refresh);
        if (rm.ok) setMembers((await rm.json()).members as Member[]);
      } else {
        setMembers([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  const summary = useMemo(() => {
    if (!members) return "";
    const actives = members.filter((m) => m.professional?.isActive).length;
    return `${actives}/${members.length} activos`;
  }, [members]);

  async function createOrg() {
    setBusy(true);
    setErr(null);
    try {
      const r = await fetchWithSessionSync(
        "/api/organizations",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: createName, slug: createSlug }),
        },
        refresh,
      );
      if (!r.ok) {
        const j = (await r.json().catch(() => ({}))) as { error?: string };
        setErr(j.error ?? "No se pudo crear la organización.");
        return;
      }
      await refresh();
      const rr = await fetchWithSessionSync("/api/organizations/me", { credentials: "include" }, refresh);
      if (!rr.ok) return;
      const j = (await rr.json()) as { organization: Org | null; role: string | null };
      setOrg(j.organization);
      setRole(j.role);
      if (j.organization) {
        const rm = await fetchWithSessionSync(`/api/organizations/${j.organization.id}/members`, { credentials: "include" }, refresh);
        if (rm.ok) setMembers((await rm.json()).members as Member[]);
      }
    } catch {
      setErr("Error al crear la organización.");
    } finally {
      setBusy(false);
    }
  }

  async function invite() {
    if (org === "loading" || org == null) return;
    setInviteBusy(true);
    setErr(null);
    try {
      const r = await fetchWithSessionSync(
        `/api/organizations/${org.id}/invite`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: inviteEmail, role: inviteRole }),
        },
        refresh,
      );
      if (!r.ok) {
        const j = (await r.json().catch(() => ({}))) as { error?: string };
        setErr(j.error ?? "No se pudo invitar.");
        return;
      }
      setInviteEmail("");
    } catch {
      setErr("Error al invitar.");
    } finally {
      setInviteBusy(false);
    }
  }

  async function toggleActive(professionalId: string, nextActive: boolean) {
    if (org === "loading" || org == null) return;
    setErr(null);
    setBusy(true);
    try {
      const r = await fetchWithSessionSync(
        `/api/organizations/${org.id}/professionals/${professionalId}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ isActive: nextActive }),
        },
        refresh,
      );
      if (!r.ok) {
        const j = (await r.json().catch(() => ({}))) as { error?: string };
        setErr(j.error ?? "No se pudo actualizar.");
        return;
      }
      const rm = await fetchWithSessionSync(`/api/organizations/${org.id}/members`, { credentials: "include" }, refresh);
      if (rm.ok) setMembers((await rm.json()).members as Member[]);
    } catch {
      setErr("Error al actualizar.");
    } finally {
      setBusy(false);
    }
  }

  if (org === "loading" || members === null) {
    return (
      <DashboardPageShell eyebrow="Mi equipo" title="Mi equipo">
        <LoadingFrame variant="light" minHeight={320} />
      </DashboardPageShell>
    );
  }

  return (
    <DashboardPageShell
      eyebrow="Mi equipo"
      title="Mi equipo"
      description="Organizaciones multi-staff: invitá profesionales, administrá miembros y mirá agendas."
    >
      {org == null ? (
        <Card className={cx(cf.dashSurface, css({ p: "5" }))}>
          <h2 className={css({ fontSize: "sm", fontWeight: "semibold", color: "#0f172a", mb: "3" })}>Crear organización</h2>
          <div className={css({ display: "grid", gridTemplateColumns: { base: "1fr", md: "1fr 1fr" }, gap: "4" })}>
            <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
              <span className={css({ fontSize: "xs", color: "#64748b" })}>Nombre</span>
              <Input value={createName} onChange={(e) => setCreateName(e.target.value)} placeholder="Ej. Clínica XYZ" />
            </label>
            <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
              <span className={css({ fontSize: "xs", color: "#64748b" })}>Slug (URL)</span>
              <Input value={createSlug} onChange={(e) => setCreateSlug(e.target.value.toLowerCase())} placeholder="clinica-xyz" />
            </label>
          </div>
          {err ? <p className={css({ mt: "3", fontSize: "sm", color: "#b91c1c" })}>{err}</p> : null}
          <div className={css({ display: "flex", gap: "3", mt: "4" })}>
            <Button variant="secondary" onClick={() => void 0} disabled>
              Cancelar
            </Button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void createOrg()}
              className={css({
                px: "4",
                py: "2.5",
                rounded: "lg",
                fontSize: "sm",
                fontWeight: "semibold",
                color: "white",
                border: "none",
                cursor: busy ? "wait" : "pointer",
                background: "linear-gradient(90deg, token(colors.brand.600), token(colors.brand.500))",
                opacity: busy ? 0.8 : 1,
              })}
            >
              {busy ? "Creando…" : "Crear organización"}
            </button>
          </div>
        </Card>
      ) : (
        <div className={css({ display: "flex", flexDir: "column", gap: "6" })}>
          <Card className={cx(cf.dashSurface, css({ p: "5" }))}>
            <h2 className={css({ fontSize: "sm", fontWeight: "semibold", color: "#0f172a", mb: "2" })}>{org.name}</h2>
            <p className={css({ fontSize: "sm", color: "#64748b", mb: "3" })}>
              URL pública: <strong>{`/org/${org.slug}/booking`}</strong> · {summary}
            </p>

            <div className={css({ display: "flex", flexWrap: "wrap", gap: "4", alignItems: "end" })}>
              <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
                <span className={css({ fontSize: "xs", color: "#64748b" })}>Invitar por email</span>
                <Input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="prof@dominio.com" />
              </label>
              <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
                <span className={css({ fontSize: "xs", color: "#64748b" })}>Rol</span>
                <select className={cf.input} value={inviteRole} onChange={(e) => setInviteRole(e.target.value as any)}>
                  <option value="admin">admin</option>
                  <option value="member">member</option>
                </select>
              </label>
              <button
                type="button"
                disabled={!canManage || inviteBusy}
                onClick={() => void invite()}
                className={css({
                  px: "4",
                  py: "2.5",
                  rounded: "lg",
                  fontSize: "sm",
                  fontWeight: "semibold",
                  color: "white",
                  border: "none",
                  cursor: !canManage || inviteBusy ? "not-allowed" : "pointer",
                  background: "linear-gradient(90deg, token(colors.brand.600), token(colors.brand.500))",
                  opacity: !canManage || inviteBusy ? 0.7 : 1,
                })}
              >
                {inviteBusy ? "Enviando…" : "Invitar profesional"}
              </button>
            </div>
            {err ? <p className={css({ mt: "3", fontSize: "sm", color: "#b91c1c" })}>{err}</p> : null}
          </Card>

          <Card className={cx(cf.dashSurface, css({ p: "5" }))}>
            <h2 className={css({ fontSize: "sm", fontWeight: "semibold", color: "#0f172a", mb: "3" })}>Miembros</h2>
            <ul className={css({ listStyle: "none", p: 0, m: 0, display: "flex", flexDir: "column", gap: "3" })}>
              {members.map((m) => (
                <li key={m.userId} className={css({ display: "flex", gap: "3", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" })}>
                  <div>
                    <p className={css({ fontWeight: "semibold", color: "#0f172a" })}>
                      {m.professional ? m.professional.name : m.email}{" "}
                      <span className={css({ fontSize: "xs", fontWeight: "medium", color: "#64748b" })}>· {m.role}</span>
                    </p>
                    <p className={css({ fontSize: "sm", color: "#64748b" })}>{m.professional ? `@${m.professional.slug}` : "Pendiente (aún no aceptó)"}</p>
                  </div>
                  <div className={css({ display: "flex", gap: "2", flexWrap: "wrap" })}>
                    {m.professional ? (
                      <>
                        <label className={css({ display: "flex", alignItems: "center", gap: "2", fontSize: "sm", color: "#334155" })}>
                          <input
                            type="checkbox"
                            checked={m.professional.isActive}
                            disabled={!canManage || busy}
                            onChange={(e) => void toggleActive(m.professional!.id, e.target.checked)}
                          />
                          {m.professional.isActive ? "Activo" : "Inactivo"}
                        </label>
                        <Link to={`/dashboard/appointments?professionalId=${m.professional.id}`}>
                          <Button variant="secondary">Ver turnos</Button>
                        </Link>
                        <Link to={`/book/${m.professional.slug}`}>
                          <Button variant="secondary">Página pública</Button>
                        </Link>
                      </>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      )}
    </DashboardPageShell>
  );
}

